import { z } from 'zod';
import { createModel, imageWithUploadSchema, QueryMany, partySchema } from '../../common';
import { nameSchema } from '../../validation';
import { vehicleClassificationSchema } from './vehicle.classification.model';
import { vehicleRegistrationSchema } from './vehicle.registration.model';
import { vehicleStatusSchema } from './vehicle.status.model';
import { vehicleDriverSchema } from './vehicle.driver.model';
import { UTagSharedSchema } from '../utag';
import { vehicleMetaSchema } from './vehicle.meta.model';

/** `vehicle db model` */
export const VehicleSchema = createModel({
  /** any title that can be added by the user. Ex `My Car` */
  title: nameSchema.optional(),
  status: vehicleStatusSchema,
  /** the classification of the vehicle such as brand, year, group, etc */
  classification: vehicleClassificationSchema,

  /** the registration info of the vehicle such as plate, id, etc*/
  registration: vehicleRegistrationSchema,
  /**
   * the organization that this Vehicle belongs to.
   *
   * if empty, then it means the it belongs to a freelancer driver
   * */
  org: partySchema.optional(),
  /**
   * The CRM client this truck belongs to, when it is NOT the Org's own.
   * Same shape and meaning as `Driver.client` — a `target: 'client'`
   * party with the client's `roles[]`. Set with the driver it arrived
   * with (a client's driver brings that client's truck); a truck rented
   * without a driver can carry it alone. `org` keeps meaning "who
   * manages this row". See `docs/adr/007-third-party-drivers.md`.
   */
  client: partySchema.optional(),
  /**
   * the current driver assigned to the vehicle.
   *
   * if empty, then it means there is no driver assigned to this vehicle yet.
   */
  driver: vehicleDriverSchema.optional(),
  /** images of the vehicle itself */
  images: z.array(imageWithUploadSchema),
  UTags: z.array(UTagSharedSchema),

  /**
   * Agent-facing signal layer — see
   * [`docs/ai/user-signals.md`](../../../../../docs/ai/user-signals.md).
   * Mirrors `Driver.meta.lastKnownLocation` so a vehicle-centric
   * fleet map can render without a Driver join.
   */
  meta: vehicleMetaSchema.optional(),
});

/** `vehicle db model` */
export type Vehicle = z.infer<typeof VehicleSchema.model>;
export type VehicleData = z.infer<typeof VehicleSchema.data>;
export type VehiclePartial = z.infer<typeof VehicleSchema.partial>;
export type VehicleQueryMany = QueryMany<Vehicle>;
